<template>
  <div ref="root" class="relative inline-block w-full sm:w-auto">
    <button
      type="button"
      class="inline-flex items-center justify-between w-full gap-2 h-9 px-3 text-xs transition bg-white border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-primary/40 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
      :class="open ? 'border-primary ring-2 ring-primary/40' : ''"
      :style="{ minWidth }"
      :aria-expanded="open"
      @click.stop="toggle"
    >
      <span class="inline-flex items-center gap-1.5 min-w-0 truncate">
        <i class="mdi mdi-calendar-range text-[15px] text-primary/70 shrink-0"></i>
        <span class="truncate">{{ displayLabel }}</span>
      </span>
      <span class="inline-flex items-center gap-1 shrink-0">
        <span
          v-if="hasValue"
          role="button"
          tabindex="0"
          class="flex items-center justify-center w-5 h-5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
          :title="clearLabel"
          @click.stop="clear"
          @keydown.enter.prevent.stop="clear"
          @keydown.space.prevent.stop="clear"
        >
          <i class="mdi mdi-close text-[14px]"></i>
        </span>
        <i
          class="mdi mdi-chevron-down text-[14px] text-slate-400 transition-transform"
          :class="open ? 'rotate-180 text-primary' : ''"
        ></i>
      </span>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        class="fixed z-[90] p-3 bg-white border shadow-lg rounded-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700"
        :style="menuStyle"
        @click.stop
      >
        <p
          v-if="label"
          class="mb-2 text-[10px] font-semibold tracking-wide uppercase text-slate-500 dark:text-slate-400"
        >
          {{ label }}
        </p>

        <div
          class="flex items-center gap-2 p-2 border rounded-lg bg-slate-50/80 dark:bg-slate-800/50 border-slate-200/80 dark:border-slate-700/80"
          :class="isRtl ? 'flex-row-reverse' : ''"
        >
          <label class="flex-1 min-w-0">
            <span class="mb-1 block text-[10px] font-medium text-slate-500">
              {{ fromLabel }}
            </span>
            <input
              ref="fromInput"
              v-model="draftFrom"
              type="date"
              class="w-full h-9 px-2 text-xs border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700"
              :max="draftTo || undefined"
            />
          </label>

          <span
            class="flex items-center justify-center w-6 h-6 mt-4 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-400 shrink-0"
            aria-hidden="true"
          >
            <i
              class="mdi text-[14px]"
              :class="isRtl ? 'mdi-arrow-left' : 'mdi-arrow-right'"
            ></i>
          </span>

          <label class="flex-1 min-w-0">
            <span class="mb-1 block text-[10px] font-medium text-slate-500">
              {{ toLabel }}
            </span>
            <input
              v-model="draftTo"
              type="date"
              class="w-full h-9 px-2 text-xs border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700"
              :min="draftFrom || undefined"
            />
          </label>
        </div>

        <p
          v-if="rangeError"
          class="mt-2 text-[10px] text-rose-500"
        >
          {{ rangeError }}
        </p>

        <div
          class="flex items-center justify-end gap-2 mt-3"
          :class="isRtl ? 'flex-row-reverse' : ''"
        >
          <button
            type="button"
            class="h-8 px-2.5 text-[11px] font-medium rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            @click="clear"
          >
            {{ clearLabel }}
          </button>
          <button
            type="button"
            class="h-8 px-3 text-[11px] font-semibold text-white rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50"
            :disabled="Boolean(rangeError)"
            @click="apply"
          >
            {{ applyLabel }}
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";

const props = defineProps({
  from: { type: String, default: "" },
  to: { type: String, default: "" },
  label: { type: String, default: "" },
  fromLabel: { type: String, default: "From" },
  toLabel: { type: String, default: "To" },
  placeholder: { type: String, default: "Select date range" },
  applyLabel: { type: String, default: "Apply" },
  clearLabel: { type: String, default: "Clear" },
  minWidth: { type: String, default: "14rem" },
  lang: { type: String, default: "ar" },
});

const emit = defineEmits(["update:from", "update:to", "change"]);

const open = ref(false);
const root = ref(null);
const fromInput = ref(null);
const menuStyle = ref({});
const draftFrom = ref("");
const draftTo = ref("");

const isRtl = computed(() => props.lang === "ar");

const hasValue = computed(() => Boolean(props.from || props.to));

const rangeError = computed(() => {
  if (!draftFrom.value || !draftTo.value) return "";
  if (draftFrom.value > draftTo.value) {
    return isRtl.value
      ? "تاريخ البداية يجب أن يكون قبل تاريخ النهاية"
      : "Start date must be before end date";
  }
  return "";
});

const displayLabel = computed(() => {
  if (props.from && props.to) {
    return `${formatDate(props.from)} – ${formatDate(props.to)}`;
  }
  if (props.from) {
    return isRtl.value
      ? `من ${formatDate(props.from)}`
      : `From ${formatDate(props.from)}`;
  }
  if (props.to) {
    return isRtl.value
      ? `إلى ${formatDate(props.to)}`
      : `Until ${formatDate(props.to)}`;
  }
  return props.placeholder;
});

function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function syncDraft() {
  draftFrom.value = props.from || "";
  draftTo.value = props.to || "";
}

function positionMenu() {
  const btn = root.value?.querySelector("button");
  if (!btn) return;

  const rect = btn.getBoundingClientRect();
  const width = Math.max(rect.width, 300);
  const gap = 4;
  const edge = 8;
  const estimatedH = 180;

  let top = rect.bottom + gap;
  if (top + estimatedH > window.innerHeight - edge) {
    top = Math.max(edge, rect.top - estimatedH - gap);
  }

  let left = rect.left;
  if (left + width > window.innerWidth - edge) {
    left = window.innerWidth - width - edge;
  }
  left = Math.max(edge, left);

  menuStyle.value = {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    width: `${Math.round(width)}px`,
  };
}

async function toggle() {
  if (open.value) {
    open.value = false;
    return;
  }
  syncDraft();
  open.value = true;
  await nextTick();
  positionMenu();
  fromInput.value?.focus();
}

function apply() {
  if (rangeError.value) return;
  emit("update:from", draftFrom.value);
  emit("update:to", draftTo.value);
  emit("change", { from: draftFrom.value, to: draftTo.value });
  open.value = false;
}

function clear() {
  draftFrom.value = "";
  draftTo.value = "";
  emit("update:from", "");
  emit("update:to", "");
  emit("change", { from: "", to: "" });
  open.value = false;
}

function onDocClick(e) {
  if (root.value && !root.value.contains(e.target)) open.value = false;
}

function onKeydown(e) {
  if (e.key === "Escape") open.value = false;
}

function onReposition() {
  if (open.value) positionMenu();
}

watch(
  () => [props.from, props.to],
  () => {
    if (!open.value) syncDraft();
  },
);

onMounted(() => {
  syncDraft();
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onReposition);
  window.addEventListener("scroll", onReposition, true);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick);
  document.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", onReposition);
  window.removeEventListener("scroll", onReposition, true);
});
</script>
